import { Avatar as RadixAvatar } from 'radix-ui';

import { cn } from '@/utils/cn';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const SIZES: Record<AvatarSize, string> = {
  xs: 'h-6 w-6 text-caption',
  sm: 'h-8 w-8 text-body-sm',
  md: 'h-10 w-10 text-body',
  lg: 'h-14 w-14 text-section-title',
  xl: 'h-20 w-20 text-page-title',
};

export interface AvatarProps {
  src?: string | null;
  /** The person's name. Used for the alt text and to derive the initials. */
  name: string;
  size?: AvatarSize;
  className?: string;
}

export const Avatar = ({ src, name, size = 'md', className }: AvatarProps) => (
  <RadixAvatar.Root
    className={cn(
      'relative flex shrink-0 overflow-hidden rounded-full bg-surface-sunken',
      SIZES[size],
      className,
    )}
  >
    {src ? (
      <RadixAvatar.Image src={src} alt={name} className="h-full w-full object-cover" />
    ) : null}
    <RadixAvatar.Fallback
      // A short delay stops a placeholder flashing over a cached photo, which on a
      // list of two hundred members reads as the whole page flickering.
      delayMs={src ? 300 : 0}
      className="flex h-full w-full items-center justify-center font-medium text-fg-secondary"
    >
      {initials(name)}
    </RadixAvatar.Fallback>
  </RadixAvatar.Root>
);

const initials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
